import type { Land } from "./lands";

/*
 * One fragment shader per land. Each paints its scene back to front
 * (sky, far, mid, near layers), then `finish` grades it into the dark
 * near-black palette of the page so text on top stays readable.
 * Coordinates: p.y runs 0..1 bottom to top, p.x is centered and
 * aspect-corrected. `t` is seconds of ambient time.
 */

export const VERTEX_SHADER = `#version 300 es
in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const PRELUDE = `#version 300 es
precision highp float;
uniform vec2 uRes;
uniform float uTime;
out vec4 fragColor;

const vec3 BG = vec3(0.016, 0.016, 0.02);

float hash11(float p) { p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 hash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), u.x),
             mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), u.x), u.y);
}
float noise1(float x) {
  float i = floor(x);
  float f = fract(x);
  return mix(hash11(i), hash11(i + 1.0), f * f * (3.0 - 2.0 * f));
}
const mat2 ROT = mat2(1.6, 1.2, -1.2, 1.6);
float fbm(vec2 p) {
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 5; i++) { s += a * noise(p); p = ROT * p; a *= 0.5; }
  return s;
}
float fbm3(vec2 p) {
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 3; i++) { s += a * noise(p); p = ROT * p; a *= 0.5; }
  return s;
}
float fbm1(float x) {
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 5; i++) { s += a * noise1(x); x = x * 2.03 + 1.7; a *= 0.5; }
  return s;
}
float ridge1(float x) {
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 5; i++) { s += a * (1.0 - abs(noise1(x) * 2.0 - 1.0)); x = x * 2.1 + 3.1; a *= 0.48; }
  return s;
}
/** Coverage of a layer whose top edge is at height h. */
float band(float y, float h, float w) { return 1.0 - smoothstep(h - w, h + w, y); }

/** Sparse glowing points on a grid, moved by drift. */
float motes(vec2 p, float scale, vec2 drift, float density, float seed, float t) {
  vec2 q = p * scale + drift;
  vec2 id = floor(q);
  vec2 f = fract(q) - 0.5;
  float h = hash12(id + seed);
  if (h > density) return 0.0;
  vec2 o = (hash22(id + seed * 3.1) - 0.5) * 0.6;
  o += 0.12 * vec2(sin(t * 0.7 + h * 40.0), cos(t * 0.5 + h * 23.0));
  float d = length(f - o);
  float tw = 0.55 + 0.45 * sin(t * (1.0 + h * 2.0) + h * 60.0);
  return tw * (smoothstep(0.06, 0.0, d) + 0.4 * exp(-d * d * 90.0));
}

float sdSeg(vec2 p, vec2 a, vec2 b, float ra, float rb) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h) - mix(ra, rb, h);
}

/** Neutral grade: soft filmic shoulder, then a black scrim and vignette so text stays readable. */
vec3 finish(vec3 col, vec2 uv) {
  col = 1.0 - exp(-col * 1.3);
  vec2 v = (uv - 0.5) * vec2(1.1, 1.0);
  col *= 1.0 - smoothstep(0.25, 1.0, length(v)) * 0.5;
  col = mix(BG, col, 0.74);
  col = mix(col, BG, smoothstep(0.7, 1.0, uv.y) * 0.5);
  col = mix(col, BG, smoothstep(0.3, 0.0, uv.y) * 0.3);
  col += (hash12(gl_FragCoord.xy + fract(uTime * 7.0) * 113.0) - 0.5) / 160.0;
  return col;
}
`;

const MAIN = `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  vec2 p = vec2((uv.x - 0.5) * uRes.x / uRes.y, uv.y);
  fragColor = vec4(finish(scene(p, uTime), uv), 1.0);
}
`;

const PLAINS = `
vec3 scene(vec2 p, float t) {
  float cam = t * 0.012;
  float hor = 0.4;
  float sy = clamp((p.y - hor) / (1.0 - hor), 0.0, 1.0);
  vec3 low = vec3(1.0, 0.64, 0.3);
  vec3 col = mix(low, vec3(0.6, 0.32, 0.26), smoothstep(0.0, 0.3, sy));
  col = mix(col, vec3(0.12, 0.07, 0.2), smoothstep(0.2, 0.95, sy));

  vec2 sun = vec2(-0.32, hor + 0.1);
  vec2 sd = p - sun;
  float d = length(sd);
  col += vec3(1.0, 0.7, 0.36) * (exp(-d * 3.2) * 0.5 + exp(-d * d * 220.0) * 0.7);
  col += vec3(1.0, 0.86, 0.62) * smoothstep(0.032, 0.024, d) * 1.4;
  float ang = atan(sd.y, sd.x);
  float rays = noise(vec2(ang * 5.0, t * 0.03)) * 0.6 + noise(vec2(ang * 13.0 + 4.0, -t * 0.02)) * 0.4;
  rays = smoothstep(0.4, 0.95, rays);
  col += vec3(1.0, 0.78, 0.45) * rays * 0.22 * exp(-d * 2.0) * smoothstep(hor - 0.02, hor + 0.05, p.y);

  vec2 cp = vec2(p.x * 1.4 + t * 0.008, (p.y - hor) * 5.5);
  float c = fbm(cp + vec2(0.0, fbm3(cp * 0.6 + t * 0.01)));
  c = smoothstep(0.5, 0.8, c) * smoothstep(hor + 0.03, hor + 0.2, p.y);
  vec3 cc = mix(vec3(1.0, 0.6, 0.36), vec3(0.3, 0.18, 0.28), smoothstep(0.05, 0.5, sy));
  cc += vec3(1.0, 0.7, 0.4) * exp(-d * 4.0) * 0.6;
  col = mix(col, cc, c * 0.75);

  float h1 = hor + 0.015 + 0.05 * fbm1(p.x * 1.6 + cam * 0.25 + 11.0);
  col = mix(col, mix(vec3(0.62, 0.42, 0.3), low, 0.45), band(p.y, h1, 0.003));
  col = mix(col, low * 0.95, exp(-abs(p.y - hor - 0.01) * 40.0) * 0.35);

  float x2 = p.x + cam * 0.55;
  float h2 = hor - 0.03 + 0.09 * fbm1(x2 * 1.1 + 3.0);
  vec3 mid = vec3(0.5, 0.33, 0.13) * (0.65 + 0.6 * fbm(vec2(x2 * 9.0, p.y * 40.0)));
  mid = mix(mid, low * 0.75, 0.45 * smoothstep(h2 - 0.12, h2, p.y));
  col = mix(col, mid, band(p.y, h2, 0.0025));

  float x3 = p.x + cam;
  float h3 = 0.2 + 0.06 * fbm1(x3 * 0.9 + 7.0);
  float wind = sin(t * 0.9 + x3 * 4.0) * 0.5 + sin(t * 1.7 + x3 * 9.0) * 0.25;
  float lift = clamp((p.y - h3 + 0.06) / 0.1, 0.0, 1.0);
  float bx = x3 * 140.0 + wind * lift * lift * 3.0;
  float n = noise(vec2(bx, 0.5)) * 0.6 + noise(vec2(bx * 2.3 + 5.0, 1.5)) * 0.4;
  float top3 = h3 + 0.05 * pow(n, 2.5);
  float depth = clamp((h3 - p.y) / 0.2, 0.0, 1.0);
  vec3 near = mix(vec3(0.55, 0.36, 0.12), vec3(0.14, 0.08, 0.04), depth);
  near *= 0.8 + 0.4 * fbm3(vec2(x3 * 22.0, p.y * 16.0));
  near += vec3(1.0, 0.7, 0.32) * smoothstep(0.03, 0.0, top3 - p.y) * 0.45;
  col = mix(col, near, band(p.y, top3, 0.002));

  float x4 = p.x + cam * 1.8;
  float tuft = smoothstep(0.35, 0.75, fbm1(x4 * 1.6 + 2.0));
  float wind4 = sin(t * 1.1 + x4 * 3.0) * 0.6;
  float lift4 = clamp((p.y - 0.04) / 0.2, 0.0, 1.0);
  float n4 = noise(vec2(x4 * 70.0 + wind4 * lift4 * lift4 * 4.0, 2.5));
  float top4 = -0.03 + tuft * (0.12 + 0.14 * pow(n4, 3.0));
  vec3 fore = vec3(0.05, 0.03, 0.02) + vec3(0.9, 0.6, 0.25) * smoothstep(0.015, 0.0, top4 - p.y) * 0.35 * tuft;
  col = mix(col, fore, band(p.y, top4, 0.002));

  float seeds = motes(p, 16.0, vec2(-t * 0.25, -t * 0.06), 0.3, 1.0, t);
  col += vec3(1.0, 0.85, 0.55) * seeds * 0.6 * smoothstep(0.75, 0.35, p.y);
  return col * 0.78;
}
`;

const ISLAND = `
vec3 scene(vec2 p, float t) {
  float cam = t * 0.01;
  float hor = 0.42;
  float sy = clamp((p.y - hor) / (1.0 - hor), 0.0, 1.0);
  vec3 low = vec3(0.5, 0.72, 0.84);
  vec3 midc = vec3(0.16, 0.32, 0.52);
  vec3 sky = mix(low, midc, smoothstep(0.0, 0.35, sy));
  sky = mix(sky, vec3(0.03, 0.05, 0.15), smoothstep(0.25, 1.0, sy));
  vec2 moon = vec2(0.1, 0.78);
  float md = length(p - moon);
  sky += vec3(0.6, 0.8, 1.0) * exp(-md * 6.0) * 0.28;
  sky = mix(sky, vec3(1.1, 1.15, 1.2), smoothstep(0.03, 0.026, md));
  vec2 cp = vec2(p.x * 1.2 + t * 0.006, (p.y - hor) * 4.0);
  float c = smoothstep(0.45, 0.8, fbm(cp + fbm3(cp * 0.7 - t * 0.01))) * smoothstep(hor, hor + 0.2, p.y);
  vec3 cc = mix(vec3(0.35, 0.45, 0.6), vec3(0.1, 0.13, 0.26), sy) + vec3(0.4, 0.5, 0.6) * exp(-md * 5.0) * 0.5;
  vec3 col = mix(sky, cc, c * 0.7);

  if (p.y < hor) {
    float dy = hor - p.y;
    float z = 0.06 / dy;
    vec2 wp = vec2(p.x * z * 3.0, z * 2.0 + t * 0.12);
    float w1 = fbm(wp + vec2(t * 0.05, 0.0));
    float w2 = fbm(wp + vec2(t * 0.05, 0.04));
    float slope = (w2 - w1) * 25.0;
    vec3 refl = mix(low, midc, clamp(dy * 2.0 + slope * 0.3, 0.0, 1.0));
    vec3 sea = mix(vec3(0.02, 0.06, 0.11), refl, 0.22 + 0.55 * exp(-dy * 7.0));
    sea *= 0.8 + 0.4 * w1;
    float glit = smoothstep(0.68, 0.95, noise(vec2(p.x * z * 7.0, z * 45.0 - t * 0.7)));
    glit *= exp(-abs(p.x - moon.x) * (4.0 + 18.0 * dy) / (1.0 + dy * 2.0));
    sea += vec3(0.8, 0.9, 1.0) * glit * 0.9;
    sea = mix(sea, vec3(0.6, 0.72, 0.8), smoothstep(0.6, 0.7, w1) * smoothstep(0.0, 0.25, dy) * 0.35);
    col = sea;
  }
  col = mix(col, low * 0.85, exp(-abs(p.y - hor) * 30.0) * 0.5);

  float xs = p.x + cam * 0.3;
  float st = smoothstep(0.07, 0.03, abs(xs + 0.62)) * 0.13 + smoothstep(0.035, 0.015, abs(xs + 0.48)) * 0.07;
  float hs = hor + st * (0.8 + 0.3 * fbm1(xs * 30.0)) - 0.004;
  col = mix(col, mix(vec3(0.1, 0.17, 0.26), low * 0.7, 0.5), band(p.y, hs, 0.003) * step(hor - 0.01, p.y));

  float xc = p.x + cam * 0.6;
  float base = 0.2;
  float xr = xc + 0.05 * (fbm1(p.y * 9.0 + 2.0) - 0.5) + 0.03 * sin(p.y * 5.0);
  float rise = smoothstep(0.26, 0.52, xr);
  float H = base + 0.46 * rise + 0.05 * ridge1(xc * 5.0 + 3.0) * rise;
  float cliff = band(p.y, H, 0.003) * step(base - 0.01, p.y);
  float strata = fbm3(vec2(xc * 4.0, p.y * 28.0));
  vec3 rock = vec3(0.045, 0.06, 0.085) * (0.55 + 0.9 * fbm(vec2(xc * 9.0, p.y * 5.0)) * (0.6 + 0.6 * strata));
  rock += vec3(0.3, 0.42, 0.52) * smoothstep(0.025, 0.0, H - p.y) * 0.45 * (1.0 - smoothstep(0.3, 0.6, xr));
  rock = mix(rock, vec3(0.04, 0.08, 0.07), smoothstep(H - 0.02, H, p.y) * rise * 0.7);
  rock = mix(rock, low * 0.5, smoothstep(H - 0.25, H, p.y) * (1.0 - rise) * 0.3);
  col = mix(col, rock, cliff);

  float foamZone = smoothstep(0.2, 0.32, xc) * exp(-abs(p.y - base) * 45.0) * step(p.y, base + 0.03);
  float foam = smoothstep(0.4, 0.75, fbm(vec2(xc * 22.0 - t * 0.3, p.y * 40.0 + t * 0.9)));
  float surge = 0.55 + 0.45 * sin(t * 0.7 + xc * 4.0);
  col = mix(col, vec3(0.72, 0.84, 0.9), foamZone * foam * surge * 0.75);

  float mist = fbm(vec2(p.x * 1.8 + t * 0.02, p.y * 5.0 - t * 0.01));
  col = mix(col, vec3(0.45, 0.58, 0.68), smoothstep(0.4, 0.75, mist) * smoothstep(0.75, 0.35, p.y) * 0.45);

  col += vec3(0.75, 0.9, 1.0) * motes(p, 22.0, vec2(-t * 0.6, -t * 0.25), 0.18, 7.0, t) * 0.35 * smoothstep(0.35, 0.1, p.y);
  return col * 0.85;
}
`;

const SWAMP = `
float deadTree(vec2 p, float s, float flip) {
  p /= s;
  p.x *= flip;
  p.x += (noise(vec2(p.y * 7.0, flip * 3.0)) - 0.5) * 0.05;
  float d = sdSeg(p, vec2(0.0, -0.3), vec2(0.05, 1.0), 0.055, 0.012);
  d = min(d, sdSeg(p, vec2(0.02, 0.42), vec2(-0.27, 0.74), 0.022, 0.004));
  d = min(d, sdSeg(p, vec2(-0.16, 0.6), vec2(-0.22, 0.92), 0.01, 0.003));
  d = min(d, sdSeg(p, vec2(0.035, 0.6), vec2(0.3, 0.88), 0.02, 0.004));
  d = min(d, sdSeg(p, vec2(0.2, 0.78), vec2(0.36, 0.8), 0.008, 0.002));
  d = min(d, sdSeg(p, vec2(0.045, 0.82), vec2(-0.07, 1.1), 0.012, 0.003));
  d = min(d, sdSeg(p, vec2(-0.1, 0.68), vec2(-0.36, 0.66), 0.008, 0.002));
  d = min(d, sdSeg(p, vec2(0.0, 0.08), vec2(-0.12, -0.1), 0.03, 0.01));
  d = min(d, sdSeg(p, vec2(0.02, 0.08), vec2(0.16, -0.1), 0.03, 0.01));
  return d * s;
}

vec3 swampSky(vec2 p, float t, vec2 moon) {
  float hor = 0.4;
  float sy = clamp((p.y - hor) / (1.0 - hor), 0.0, 1.0);
  vec3 sky = mix(vec3(0.16, 0.2, 0.22), vec3(0.04, 0.05, 0.08), smoothstep(0.0, 0.7, sy));
  float md = length(p - moon);
  sky += vec3(0.6, 0.72, 0.75) * (exp(-md * 7.0) * 0.4 + exp(-md * 2.0) * 0.08);
  float disc = smoothstep(0.045, 0.04, md);
  float crater = fbm3((p - moon) * 60.0);
  sky = mix(sky, vec3(1.05, 1.08, 1.0) * (0.85 + 0.25 * crater), disc);
  vec2 cp = vec2(p.x * 1.5 + t * 0.012, (p.y - hor) * 4.5);
  float c = smoothstep(0.42, 0.78, fbm(cp + fbm3(cp - t * 0.015)));
  vec3 cc = vec3(0.07, 0.08, 0.1) + vec3(0.35, 0.42, 0.45) * exp(-md * 4.0) * 0.8;
  return mix(sky, cc, c * 0.85 * smoothstep(hor, hor + 0.15, p.y));
}

float swampTrees(vec2 p, float cam) {
  float d = deadTree(p - vec2(-0.62 + cam * 0.8, 0.12), 0.62, 1.0);
  d = min(d, deadTree(p - vec2(0.58 + cam * 0.8, 0.16), 0.5, -1.0));
  return d;
}

vec3 scene(vec2 p, float t) {
  float cam = sin(t * 0.03) * 0.04;
  float hor = 0.4;
  vec2 moon = vec2(-0.28, 0.76);
  vec3 col = swampSky(p, t, moon);

  float h1 = hor + 0.04 + 0.05 * fbm1(p.x * 3.0 + cam * 0.3) + 0.03 * noise1(p.x * 40.0);
  col = mix(col, vec3(0.05, 0.07, 0.08), band(p.y, h1, 0.003));
  float farTree = deadTree(p - vec2(0.12 + cam * 0.3, hor - 0.01), 0.2, 1.0);
  col = mix(col, vec3(0.07, 0.09, 0.1), smoothstep(0.002, 0.0, farTree));

  if (p.y < hor) {
    float dy = hor - p.y;
    vec2 rp = vec2(p.x, 2.0 * hor - p.y);
    rp.x += (noise(vec2(p.x * 8.0, dy * 120.0 / (dy + 0.2) - t * 0.6)) - 0.5) * 0.012;
    vec3 refl = swampSky(rp, t, moon);
    refl = mix(refl, vec3(0.05, 0.07, 0.08), band(rp.y, h1, 0.006));
    float rt = swampTrees(rp, cam);
    refl = mix(refl, vec3(0.01, 0.015, 0.015), smoothstep(0.004, 0.0, rt));
    col = refl * vec3(0.42, 0.5, 0.48);
    float scum = smoothstep(0.55, 0.8, fbm(vec2(p.x * 6.0, dy * 30.0 / (dy + 0.1))));
    col = mix(col, vec3(0.04, 0.07, 0.04), scum * 0.6);
  }

  float fogA = fbm(vec2(p.x * 1.6 + t * 0.025, p.y * 7.0));
  float fogB = fbm(vec2(p.x * 3.0 - t * 0.04, p.y * 12.0 + 4.0));
  float fog = smoothstep(0.35, 0.8, fogA * 0.65 + fogB * 0.45) * exp(-abs(p.y - hor + 0.03) * 7.0);
  col = mix(col, vec3(0.32, 0.4, 0.38), fog * 0.55);

  float tree = swampTrees(p, cam);
  vec3 bark = vec3(0.015, 0.018, 0.02) + vec3(0.3, 0.36, 0.38) * smoothstep(0.0, -0.006, tree) * 0.0;
  col = mix(col, bark, smoothstep(0.003, 0.0, tree));

  float bank = 0.14 + 0.05 * fbm1(p.x * 2.0 + 20.0);
  float reeds = noise(vec2(p.x * 160.0 + sin(t * 0.8 + p.x * 5.0) * (p.y - bank) * 60.0, 0.5));
  float rtop = bank + 0.07 * pow(reeds, 3.0) * smoothstep(0.1, 0.4, fbm1(p.x * 5.0));
  col = mix(col, vec3(0.012, 0.016, 0.014), band(p.y, rtop, 0.002));

  float lowFog = fbm(vec2(p.x * 2.0 - t * 0.03, p.y * 4.0 + t * 0.01));
  col = mix(col, vec3(0.22, 0.3, 0.28), smoothstep(0.4, 0.8, lowFog) * smoothstep(0.35, 0.08, p.y) * 0.5);

  vec2 wq = p + vec2(sin(t * 0.3) * 0.02, sin(t * 0.7) * 0.01);
  float wisp = motes(wq, 9.0, vec2(t * 0.05, 0.0), 0.12, 13.0, t) * smoothstep(0.5, 0.3, p.y) * smoothstep(0.1, 0.25, p.y);
  col += vec3(0.5, 1.0, 0.65) * wisp * 0.55;
  return col;
}
`;

const MOUNTAIN = `
/** Distance to the nearest cell border: thin values trace a crack network. */
float cracks(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float d1 = 8.0, d2 = 8.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 g = vec2(float(x), float(y));
      vec2 o = hash22(i + g);
      float d = length(g + o - f);
      if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) { d2 = d; }
    }
  }
  return d2 - d1;
}

vec3 scene(vec2 p, float t) {
  float cam = t * 0.008;
  float hor = 0.36;
  float sy = clamp((p.y - hor) / (1.0 - hor), 0.0, 1.0);
  vec3 col = mix(vec3(0.7, 0.22, 0.07), vec3(0.22, 0.05, 0.05), smoothstep(0.0, 0.35, sy));
  col = mix(col, vec3(0.04, 0.015, 0.03), smoothstep(0.3, 1.0, sy));

  vec2 vent = vec2(0.06, 0.6);
  float vd = length((p - vent) * vec2(1.0, 0.75));
  col += vec3(1.0, 0.35, 0.08) * exp(-vd * 3.5) * 0.55;

  vec2 sp = vec2(p.x * 2.0 - (p.y - vent.y) * 0.7 + t * 0.01, p.y * 2.2 - t * 0.035);
  float smoke = fbm(sp + fbm3(sp * 1.5 + t * 0.02));
  float plume = smoothstep(0.35, 0.8, smoke) * smoothstep(vent.y - 0.04, vent.y + 0.15, p.y);
  plume *= smoothstep(0.6, 0.0, abs(p.x - vent.x - (p.y - vent.y) * 0.55));
  vec3 ash = mix(vec3(0.65, 0.16, 0.04), vec3(0.05, 0.025, 0.035), smoothstep(vent.y, vent.y + 0.32, p.y));
  col = mix(col, ash, plume * 0.85);
  vec2 cp = vec2(p.x * 1.3 + t * 0.01, (p.y - hor) * 3.5);
  float c = smoothstep(0.5, 0.85, fbm(cp + fbm3(cp + t * 0.01))) * smoothstep(hor + 0.1, hor + 0.4, p.y);
  col = mix(col, vec3(0.07, 0.025, 0.035) + vec3(0.5, 0.12, 0.03) * (1.0 - sy) * 0.6, c * 0.7);

  float x1 = p.x + cam * 0.2;
  float h1 = hor - 0.03 + 0.3 * ridge1(x1 * 1.2 + 4.0) * (0.55 + 0.45 * noise1(x1 * 0.7));
  vec3 far = vec3(0.4, 0.13, 0.11) * (0.8 + 0.35 * fbm3(vec2(x1 * 18.0, p.y * 9.0)));
  far = mix(far, col, 0.35);
  col = mix(col, far, band(p.y, h1, 0.003));
  col = mix(col, vec3(0.55, 0.16, 0.07), exp(-abs(p.y - hor - 0.02) * 16.0) * 0.3);

  float x2 = p.x + cam * 0.5;
  float dx = x2 - vent.x;
  float slope = abs(dx) + 0.05 * (fbm1(x2 * 7.0 + 1.0) - 0.5) + 0.02 * ridge1(x2 * 18.0);
  float cone = vent.y - pow(slope, 0.85) * 0.95;
  cone -= smoothstep(0.05, 0.0, abs(dx)) * 0.025;
  float h2 = max(cone, hor - 0.05 + 0.4 * (fbm1(x2 * 1.3 + 9.0) - 0.47) + 0.05 * ridge1(x2 * 4.0));
  float gully = fbm(vec2(dx * 26.0 / (0.3 + vent.y - p.y), p.y * 3.0));
  vec3 mid = vec3(0.09, 0.035, 0.035) * (0.5 + 0.9 * gully);
  mid *= 0.55 + 0.6 * smoothstep(h2 - 0.25, h2, p.y);
  mid += vec3(1.0, 0.3, 0.06) * smoothstep(0.035, 0.0, h2 - p.y) * 0.45;
  float lava = 0.0;
  for (int k = 0; k < 3; k++) {
    float fk = float(k);
    float drop = vent.y - p.y;
    float lx = vent.x + (fk - 1.0) * drop * 0.55 + 0.025 * sin(drop * 22.0 + fk * 2.0) + 0.01 * (fbm1(drop * 30.0 + fk * 5.0) - 0.5);
    float width = 0.003 + drop * 0.012;
    float flow = 0.6 + 0.4 * sin(drop * 70.0 - t * 1.6 + fk);
    lava += smoothstep(width, 0.0, abs(x2 - lx)) * flow * smoothstep(0.0, 0.02, drop) * smoothstep(0.34, 0.12, drop);
  }
  mid += vec3(1.5, 0.48, 0.08) * lava * step(p.y, h2 - 0.004);
  mid += vec3(0.6, 0.12, 0.02) * exp(-abs(dx) * 9.0) * smoothstep(hor - 0.1, vent.y, p.y) * 0.35;
  col = mix(col, mid, band(p.y, h2, 0.0025));

  float x3 = p.x + cam;
  float h3 = 0.11 + 0.4 * (fbm1(x3 * 0.8 + 21.0) - 0.47) + 0.035 * ridge1(x3 * 5.0);
  vec2 cq = vec2(x3 * 7.0, p.y * 11.0);
  float cr = cracks(cq + 0.15 * vec2(fbm3(cq), fbm3(cq + 7.0)));
  float heat = fbm3(vec2(x3 * 3.0, p.y * 4.0 - t * 0.05));
  vec3 near = vec3(0.03, 0.014, 0.016) * (0.6 + 0.8 * fbm3(vec2(x3 * 24.0, p.y * 24.0)));
  float pulse = 0.55 + 0.45 * sin(t * 0.9 + x3 * 6.0 + heat * 6.0);
  float glow = smoothstep(0.07, 0.0, cr) * smoothstep(0.3, 0.7, heat) * smoothstep(h3 - 0.01, h3 - 0.08, p.y);
  near += vec3(1.4, 0.38, 0.06) * glow * pulse;
  near += vec3(0.9, 0.24, 0.05) * smoothstep(0.02, 0.0, h3 - p.y) * 0.35;
  col = mix(col, near, band(p.y, h3, 0.002));

  float e1 = motes(p, 16.0, vec2(sin(t * 0.2) * 0.5 - t * 0.12, -t * 1.0), 0.32, 3.0, t);
  float e2 = motes(p, 30.0, vec2(-t * 0.2, -t * 1.7), 0.35, 5.0, t);
  col += vec3(1.0, 0.45, 0.1) * (e1 * 1.0 + e2 * 0.6) * smoothstep(1.0, 0.25, p.y);
  return col;
}
`;

const FOREST = `
float trunks(vec2 p, float scale, float off, float seed, float chance, out float rim) {
  float x = p.x * scale + off;
  float id = floor(x);
  float h = hash11(id * 7.31 + seed);
  float cx = 0.25 + 0.5 * hash11(id * 3.17 + seed);
  float w = 0.05 + 0.12 * hash11(id * 1.93 + seed);
  float lean = (hash11(id * 5.7 + seed) - 0.5) * 0.08;
  float dx = fract(x) - cx - (p.y - 0.3) * lean * scale;
  dx += (noise(vec2(p.y * 5.0 * scale, id)) - 0.5) * 0.025;
  float wid = w * (1.0 + smoothstep(0.12, -0.1, p.y - 0.3 + 0.0) * 0.0);
  rim = smoothstep(-wid, -wid * 0.35, dx) * step(dx, -wid * 0.1);
  float m = smoothstep(wid, wid * 0.8, abs(dx));
  return h < chance ? m : 0.0;
}

vec3 scene(vec2 p, float t) {
  float cam = t * 0.006;
  vec2 src = vec2(-0.5, 1.2);
  float ld = length(p - src);
  vec3 fogCol = vec3(0.17, 0.27, 0.17);
  vec3 col = mix(vec3(0.07, 0.14, 0.1), vec3(0.26, 0.38, 0.22), exp(-ld * 1.3));
  col += vec3(0.9, 0.85, 0.5) * exp(-ld * 2.6) * 0.3;

  vec2 dir = normalize(vec2(0.38, -1.0));
  vec2 perp = vec2(-dir.y, dir.x);
  float across = dot(p - src, perp);
  float along = dot(p - src, dir);
  float shafts = fbm3(vec2(across * 11.0 + t * 0.025, along * 0.4 - t * 0.01));
  shafts = pow(smoothstep(0.4, 0.85, shafts), 1.6) * exp(-along * 0.7);

  float rim;
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    float scale = 10.0 - fi * 2.6;
    float haze = 0.8 - fi * 0.26;
    float floorY = 0.36 - fi * 0.085 + 0.12 * (fbm1(p.x * (2.2 - fi * 0.4) + fi * 7.0 + cam * (1.0 + fi * 2.0) * 3.0) - 0.47);
    float m = trunks(p, scale, cam * (1.0 + fi * 2.0) * scale * 0.5, fi * 17.0, 0.75 - fi * 0.12, rim);
    m *= smoothstep(floorY - 0.03, floorY + 0.01, p.y);
    vec3 bark = mix(vec3(0.025, 0.04, 0.03), fogCol, haze);
    bark *= 0.8 + 0.4 * fbm3(vec2(p.x * 60.0, p.y * scale * 2.0));
    bark += vec3(0.75, 0.75, 0.4) * rim * (0.05 + 0.25 * (1.0 - haze)) * exp(-ld * 0.6);
    col = mix(col, bark, m);
    vec3 ground = mix(vec3(0.05, 0.09, 0.04), fogCol, haze) * (0.75 + 0.5 * fbm3(vec2(p.x * 30.0, p.y * 60.0)));
    col = mix(col, ground, band(p.y, floorY, 0.012) * (0.55 + 0.45 * smoothstep(floorY, floorY - 0.08, p.y)));
    col += vec3(0.9, 0.85, 0.5) * shafts * (0.1 + 0.08 * fi);
    col = mix(col, fogCol, 0.05);
  }

  float cn = fbm(vec2(p.x * 3.0 + cam * 2.0, p.y * 3.0) + t * 0.008);
  float canopy = smoothstep(0.78, 1.08, p.y + cn * 0.35);
  col = mix(col, vec3(0.012, 0.025, 0.018), canopy * 0.92);

  float fx = p.x + cam * 9.0;
  float fern = noise(vec2(fx * 45.0 + sin(t * 0.6 + fx * 3.0) * (p.y - 0.03) * 8.0, 0.5));
  float ftop = 0.02 + smoothstep(0.25, 0.65, fbm1(fx * 2.5)) * (0.06 + 0.1 * pow(fern, 2.0));
  col = mix(col, vec3(0.01, 0.022, 0.012), band(p.y, ftop, 0.003));

  float spores = motes(p, 14.0, vec2(-t * 0.08, -t * 0.12), 0.3, 11.0, t);
  float spores2 = motes(p, 28.0, vec2(t * 0.05, -t * 0.08), 0.3, 19.0, t);
  col += vec3(1.0, 0.9, 0.55) * (spores * 0.6 + spores2 * 0.35) * (0.3 + shafts * 2.0);
  return col;
}
`;

const WASTES = `
vec3 scene(vec2 p, float t) {
  float cam = t * 0.01;
  float hor = 0.4;
  float sy = clamp((p.y - hor) / (1.0 - hor), 0.0, 1.0);
  vec3 col = mix(vec3(0.5, 0.44, 0.48), vec3(0.18, 0.15, 0.22), smoothstep(0.0, 0.4, sy));
  col = mix(col, vec3(0.04, 0.035, 0.07), smoothstep(0.35, 1.0, sy));
  vec2 st = p * 70.0;
  float star = step(0.985, hash12(floor(st))) * smoothstep(0.3, 0.0, length(fract(st) - 0.5));
  col += vec3(0.8, 0.8, 0.9) * star * smoothstep(0.55, 0.9, p.y) * (0.6 + 0.4 * sin(t * 2.0 + hash12(floor(st)) * 30.0));
  vec2 moon = vec2(0.3, 0.7);
  float md = length(p - moon);
  col += vec3(0.7, 0.68, 0.75) * exp(-md * 5.0) * 0.25;
  col = mix(col, vec3(0.95, 0.93, 0.98), smoothstep(0.022, 0.018, md));
  float h1 = hor + 0.03 * fbm1(p.x * 1.4 + cam * 0.3);
  col = mix(col, vec3(0.32, 0.28, 0.32), band(p.y, h1, 0.004));
  float mono = smoothstep(0.012, 0.009, abs(p.x + 0.35 + cam * 0.3)) * band(p.y, hor + 0.09, 0.002);
  col = mix(col, vec3(0.14, 0.12, 0.16), mono);
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float x = p.x + cam * (0.6 + fi * 0.6);
    float h = hor - 0.04 - fi * 0.08 + 0.06 * sin(x * (2.0 + fi) + fi * 2.0) + 0.03 * fbm1(x * 2.0 + fi * 9.0);
    float shade = 0.5 + 0.5 * sin(x * (2.0 + fi) + fi * 2.0 + 1.2);
    vec3 dune = mix(vec3(0.12, 0.1, 0.13), vec3(0.4, 0.35, 0.36), shade * (0.6 - fi * 0.15));
    dune *= 0.85 + 0.25 * fbm3(vec2(x * 30.0, p.y * 80.0));
    col = mix(col, dune, band(p.y, h, 0.003));
  }
  float dust = fbm(vec2(p.x * 2.0 - t * 0.06, p.y * 9.0));
  col = mix(col, vec3(0.45, 0.4, 0.42), smoothstep(0.45, 0.8, dust) * smoothstep(0.5, 0.2, p.y) * 0.35);
  col += vec3(0.85, 0.8, 0.85) * motes(p, 20.0, vec2(-t * 0.4, -t * 0.03), 0.2, 23.0, t) * 0.3;
  return col;
}
`;

const SCENES: Readonly<Record<Land, string>> = {
  plains: PLAINS,
  island: ISLAND,
  swamp: SWAMP,
  mountain: MOUNTAIN,
  forest: FOREST,
  wastes: WASTES,
};

export function fragmentShader(land: Land): string {
  return PRELUDE + SCENES[land] + MAIN;
}
